import React, { useState } from 'react';
import { ChevronDown, Trash2 } from 'lucide-react';
import SpinLoader from '../../../UI/Loader/SpinLoader';
import { fmtDay } from '../../epmHelpers';
import { CHECKLIST_PROFILE } from '../../checklists';
import ChecklistAuditTable from './ChecklistAuditTable';
import { marker } from './utils';

const ProductionChecklist = ({
    productionChecks = [],
    signOffItem,
    isSigned = false,
    productionLocked = false,
    isChecklistsLoading = false,
    isProductionChecked,
    toggleCheck,
    canUpdate = true,
    setDeleteChecklistTarget,
    onOpenAddCheckModal,
    build = {},
    boundDay,
    productionAudits = [],
    checklistLookup = {},
    currentUserId,
    currentUserName,
}) => {
    const [productionOpen, setProductionOpen] = useState(!isSigned);
    const [productionAuditOpen, setProductionAuditOpen] = useState(false);

    const exp = build?.exp || {};
    const previousVersion = Math.max(1, (exp.version || 1) - 1);

    const checkText = (item) => {
        if (item.id === 'pr-rerun') {
            return build?.lost ? (
                <>
                    <strong>Re-run checks against version {exp.version}</strong> · B changed after sign-off
                </>
            ) : (
                <>
                    Checks re-run against version {exp.version}{' '}
                    <span className="text-[#8992a8]">· the current Experience</span>
                </>
            );
        }
        if (item.id === 'pr-performance') return <>Performance within budget · {build?.performance}</>;
        if (item.id === 'pr-allocation') return <>Allocation matches plan · 50/50</>;
        return item.label;
    };

    return (
        <div className="epm-plan__collapsible" id="epm-production-checklist">
            <div
                className={`epm-plan__collapsible-header relative ${productionOpen ? 'is-open' : ''}`}
                onClick={() => setProductionOpen(!productionOpen)}
            >
                <ChevronDown size={16} />
                <span className="epm-plan__collapsible-title text-sm">Production</span>
                <span className="epm-plan__collapsible-subtitle text-xs"></span>
                <span className="epm-plan__collapsible-action">Checklist</span>
            </div>
            {productionOpen && (
                <div className="epm-plan__collapsible-body">
                    {isChecklistsLoading ? (
                        <div className="flex items-center justify-center py-6 text-xs text-[#8992a8] gap-2">
                            <SpinLoader className="w-4 h-4 text-[#8992a8]" />
                            <span>Loading checklist items...</span>
                        </div>
                    ) : (
                        <>
                            {productionLocked && (
                                <div className="text-[11px] text-[#687390] mb-2">
                                    Production checks open once staging is signed off.
                                </div>
                            )}

                            {productionChecks.map((item, index) => {
                                const isChecked = isProductionChecked(item, index);
                                return (
                                    <div
                                        key={item.id || item._id || index}
                                        className={`epm-build__ck ${!isChecked ? 'open' : ''} ${productionLocked ? 'is-locked' : ''}`}
                                    >
                                        <input
                                            type="checkbox"
                                            className="epm-build__checkbox"
                                            checked={isChecked}
                                            disabled={productionLocked || !canUpdate}
                                            onChange={() => toggleCheck(item, index)}
                                            aria-label={item.label || item.name}
                                        />
                                        <span>
                                            {checkText(item)}
                                            {item.detail && <span className="epm-build__detail">{item.detail}</span>}
                                        </span>
                                        <div className="flex items-center gap-2 pt-[2px]">
                                            <span className="epm-build__req !pt-0">{marker(item)}</span>
                                            {canUpdate && item.isOneOff && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDeleteChecklistTarget(item);
                                                    }}
                                                    className="text-[#8992a8] hover:text-[#e5484d] transition-colors p-1 -m-1 rounded hover:bg-[#fff0f0] cursor-pointer flex items-center justify-center"
                                                    title="Delete one-off check"
                                                    aria-label={`Delete ${item.label || item.name}`}
                                                >
                                                    <Trash2 size={13} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            <div className={`epm-build__ck ${!isSigned ? 'open' : ''} ${productionLocked ? 'is-locked' : ''}`}>
                                <input
                                    type="checkbox"
                                    className="epm-build__checkbox"
                                    checked={isSigned}
                                    disabled={true}
                                    aria-label={signOffItem?.label || 'Production sign-off recorded'}
                                />
                                <span>
                                    {signOffItem?.label || 'Production sign-off recorded'}{' '}
                                    <span className="text-[11px] text-[#8992a8]">· ticks itself when signed below</span>
                                </span>
                                <span className="epm-build__req">{signOffItem ? marker(signOffItem) : 'REQ'}</span>
                            </div>

                            {canUpdate && (
                                <div
                                    role="button"
                                    tabIndex={0}
                                    className="epm-build__addck"
                                    onClick={onOpenAddCheckModal}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            onOpenAddCheckModal();
                                        }
                                    }}
                                >
                                    + Add a one-off check for this test
                                </div>
                            )}

                            {build?.lost && (
                                <div className="text-[11px] text-[#8992a8] mt-3">
                                    <span className="line-through">
                                        Signed off by {build?.production?.previous_signer},{' '}
                                        {fmtDay(build?.production?.previous_day)} against v{previousVersion}
                                    </span>
                                    <span className="text-[#a33c49] ml-2">
                                        cleared {fmtDay(build?.production?.lost_day)} — B changed v{previousVersion} → v
                                        {exp.version}
                                    </span>
                                </div>
                            )}

                            <div className="text-[11px] text-[#aab3c8] mt-2.5">
                                {CHECKLIST_PROFILE} template{Number.isFinite(boundDay) ? ` · snapshot taken on entering QA, ${fmtDay(boundDay)}` : ''}
                            </div>

                            <div className="epm-plan__collapsible flat mt-3">
                                <div
                                    className={`epm-plan__collapsible-header relative ${productionAuditOpen ? 'is-open' : ''}`}
                                    onClick={() => setProductionAuditOpen(!productionAuditOpen)}
                                >
                                    <ChevronDown size={14} />
                                    <span className="epm-plan__collapsible-title text-xs">Activities</span>
                                    <span className="epm-plan__collapsible-subtitle text-[11px]">
                                        {productionAudits.length} {productionAudits.length === 1 ? 'event' : 'events'}
                                    </span>
                                    <span className="epm-plan__collapsible-action">
                                        {productionAuditOpen ? 'Hide' : 'Show'}
                                    </span>
                                </div>
                                {productionAuditOpen && (
                                    <div className="epm-plan__collapsible-body pt-2 audit-table">
                                        <ChecklistAuditTable
                                            audits={productionAudits}
                                            checklistLookup={checklistLookup}
                                            currentUserId={currentUserId}
                                            currentUserName={currentUserName}
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default ProductionChecklist;

